import { Table } from '@tanstack/react-table';

import { Button } from '@components/button';
import { IconDownload } from '@components/icons/download';
import { Menu } from '@components/menu';
import { useScopedTranslate } from '@locale/translate';
import { downloadAsCsv } from '@utils/browser';

import { accessorMap } from './renderers';
import { sortByReferenceArray } from './sorters';
import { AvailableColumn, ColumnType, DataItemType } from './types';

export const ExportCsvButton = <T extends DataItemType>({
  availableColumns,
  table,
}: {
  availableColumns: Array<AvailableColumn<T>>;
  table: Table<T>;
}) => {
  const { t } = useScopedTranslate(locale);
  return (
    <Button
      size="sm"
      iconLeft={<IconDownload />}
      variant="secondary"
      text={t('export')}
      onClick={() => {
        exportTableData(availableColumns, table);
      }}
    />
  );
};

export const ExportCsvMenuItem = <T extends DataItemType>({
  availableColumns,
  table,
}: {
  availableColumns: Array<AvailableColumn<T>>;
  table: Table<T>;
}) => {
  const { t } = useScopedTranslate(locale);
  return (
    <Menu.Item
      icon={<IconDownload />}
      label={t('export')}
      onSelect={() => {
        exportTableData(availableColumns, table);
      }}
    />
  );
};

const locale = {
  en: {
    export: 'Export to CSV',
  },
  is: {
    export: 'Sækja sem CSV',
  },
};

export const DANGEROUS_START_CHARACTERS = ['=', '+', '-', '@'];

const exportTableData = <T extends DataItemType>(
  availableColumns: Array<AvailableColumn<T>>,
  table: Table<any>
) => {
  const tableState = table.getState();
  const rows = table.getRowModel().rows;
  const visibleColumns = table.getVisibleFlatColumns().map((column) => column.id);
  const orderedVisibleIds = sortByReferenceArray(visibleColumns, tableState.columnOrder);
  const orderedColumns = orderedVisibleIds
    .map((id) => availableColumns.find((column) => column.id === id))
    .filter(Boolean) as AvailableColumn<T>[];

  const headerRow = orderedColumns.map((column) => column.title).join(',');

  const sheetRows = [
    headerRow,
    ...rows.map((row) => {
      return orderedColumns
        .map((column) => {
          const getValue = column.extractValue
            ? column.extractValue
            : accessorMap[column.type as ColumnType] ?? accessorMap.text;
          const rowValue = getValue(row.original[column.id], column);

          return typeof rowValue === 'string'
            ? `"${
                DANGEROUS_START_CHARACTERS.some((char) => rowValue.startsWith(char))
                  ? `'${rowValue}`
                  : rowValue
              }"`
            : rowValue;
        })
        .join(',');
    }),
  ];
  const csvContent = sheetRows.join('\n');
  downloadAsCsv({ content: csvContent });
};
